import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
// Redux
import { useSelector } from "react-redux";
// Constants
import { pathHomePage, WEB_SERVICE_DASHBOARD_ROUTE, stationVersions } from "../../../../config/constants";
// Components
import { Title, CreateStationDiv, CreateStationLink, TableContainer, TableTitle, StationContainer, StationInfo, ActionCell, VersionSelect, ActionButton } from "./styles";
import Header from "../../../header";
import GoBack from "../../../goBack";
import Loader from "../../../loader";
import Axios from "../../../../config/axios";

const StationController = () => {
  const [isLoadingStations, setIsLoadingStations] = useState(true);
  const [stations, setStations] = useState([]);
  const [editingStationId, setEditingStationId] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState("");
  const { hospitalId } = useParams();
  const environment = useSelector(({ database }) => database.environment);
  const currentHospitalName = useSelector(({ hospital }) => hospital.currentName);

  // Fetching stations by hospital
  useEffect(() => {
    Axios("get", `${WEB_SERVICE_DASHBOARD_ROUTE}/station/get/${environment}/${hospitalId}`)
      .then(({ data }) => {
        setStations(data);
        setIsLoadingStations(false);
      })
      .catch((error) => {
        console.log("Get hospital stations: ", error);
      });
  }, [environment, hospitalId]);

  const startEditing = (station) => {
    setEditingStationId(station.STATION_ID);
    setSelectedVersion(station.TARGET_VERSION || station.CURRENT_VERSION || "");
  };

  const cancelEditing = () => {
    setEditingStationId(null);
    setSelectedVersion("");
  };

  const updateStationVersion = (station) => {
    if (!selectedVersion) return toast.error("You must select a version");

    const stationData = {
      CURRENT_VERSION: selectedVersion,
      TARGET_VERSION: selectedVersion,
    };
    Axios("put", `${WEB_SERVICE_DASHBOARD_ROUTE}/station/update/${environment}/${station.STATION_ID}`, stationData)
      .then(({ data }) => {
        if (!data.stationUpdated) return toast.error("There was an error updating the station");

        setStations((prevStations) =>
          prevStations.map((s) => (s.STATION_ID === station.STATION_ID ? { ...s, ...stationData } : s))
        );
        toast.success("Station updated successfully");
        cancelEditing();
      })
      .catch((error) => {
        console.log("Endpoint to update station: ", error);
        toast.error("There was an error updating the station");
      });
  };

  const renderNewStationButton = () => {
    return (
      <CreateStationDiv>
        <CreateStationLink to={`${pathHomePage}/create-station/${hospitalId}`}>Create new station</CreateStationLink>
      </CreateStationDiv>
    );
  };

  const renderTableHeader = () => {
    return (
      <TableContainer>
        <TableTitle>Station ID</TableTitle>
        <TableTitle>Application</TableTitle>
        <TableTitle>Current version</TableTitle>
        <TableTitle>Target version</TableTitle>
        <TableTitle>Actions</TableTitle>
      </TableContainer>
    );
  };

  const renderStations = () => {
    return stations.map((station, i) => {
      const isEditing = editingStationId === station.STATION_ID;
      return (
        <StationContainer key={i}>
          <StationInfo>{station.STATION_ID}</StationInfo>
          <StationInfo>{station.SOFTWARE}</StationInfo>
          {isEditing ? (
            <>
              <StationInfo>{selectedVersion || "-"}</StationInfo>
              <StationInfo>{selectedVersion || "-"}</StationInfo>
              <ActionCell>
                <VersionSelect value={selectedVersion} onChange={(e) => setSelectedVersion(e.target.value)}>
                  <option value=""></option>
                  {stationVersions.map((version) => (
                    <option key={version} value={version}>
                      {version}
                    </option>
                  ))}
                </VersionSelect>
                <ActionButton onClick={() => updateStationVersion(station)}>Save</ActionButton>
                <ActionButton variant="cancel" onClick={cancelEditing}>
                  Cancel
                </ActionButton>
              </ActionCell>
            </>
          ) : (
            <>
              <StationInfo>{station.CURRENT_VERSION}</StationInfo>
              <StationInfo>{station.TARGET_VERSION}</StationInfo>
              <ActionCell>
                <ActionButton onClick={() => startEditing(station)}>Edit version</ActionButton>
              </ActionCell>
            </>
          )}
        </StationContainer>
      );
    });
  };

  return (
    <>
      <Header />
      <GoBack path={`${pathHomePage}/intermediate-management/${hospitalId}`} />
      {isLoadingStations ? (
        <Loader />
      ) : (
        <>
          <Title>{`Hospital stations: ${currentHospitalName}`}</Title>
          {renderNewStationButton()}
          {stations.length ? (
            <>
              {renderTableHeader()}
              {renderStations()}
            </>
          ) : (
            <Title fontSize="20px">There are no stations registered</Title>
          )}
        </>
      )}
    </>
  );
};

export default StationController;
